import {Injectable, signal} from '@angular/core';
import {HypothesisFileTypeName} from "../../../../types/file.types";
import {
  GetHypothesisFileCommentParams, GetHypothesisFileIdParams,
  HypothesisFilesData,
  UpdateHypothesisFileCommentParams
} from "../types/hypothesis-modal.types";

@Injectable({
  providedIn: 'root'
})
export class HypothesisFilesStateService {
  private _hypothesisFilesState = signal<Record<HypothesisFileTypeName, Array<HypothesisFilesData>> | null>(null);
  public hypothesisFilesState = this._hypothesisFilesState.asReadonly();

  public setInitialHypothesisFilesState(files: Array<HypothesisFilesData>): void {
    const groupedFiles = files.reduce((acc, file) => {
      const type = file.type;
      if (!acc[type]) {
        acc[type] = [];
      }
      acc[type].push(file);
      return acc;
    }, {} as Record<HypothesisFileTypeName, HypothesisFilesData[]>);

    this._hypothesisFilesState.set(groupedFiles);
  }

  public updateHypothesisFilesState(key: HypothesisFileTypeName, value: HypothesisFilesData): void {
    this._hypothesisFilesState.update(current => {
      const newValue = current ? { ...current } : {} as Record<HypothesisFileTypeName, Array<HypothesisFilesData>>;

      newValue[key] = newValue[key] || [];

      return {
        ...newValue,
        [key]: newValue[key].some(item => item.id === value.id)
          ? newValue[key]
          : [...newValue[key], value]
      };
    });
  }

  public deleteHypothesisFilesState(key: HypothesisFileTypeName, id: number): void {
    this._hypothesisFilesState.update(current => {
      if (!current || !current[key]) { return current; }

      const newValue = { ...current };
      newValue[key] = newValue[key].filter(file => file.id !== id);

      if (!newValue[key].length) { delete newValue[key]; }

      return !Object.keys(newValue).length ? null : newValue;
    });
  }

  public getHypothesisFileId({ type, fileId }: GetHypothesisFileIdParams): number {
    return this.hypothesisFilesState()?.[type].find(file => file.file.id === fileId)?.id || 0;
  }

  public getHypothesisFileCommentByFileId({ type, fileId }: GetHypothesisFileCommentParams): string | null {
    return this.hypothesisFilesState()?.[type].find(file => file.file.id === fileId)?.comment || null;
  }

  public updateHypothesisFileCommentByFileId({ type, fileId, comment }: UpdateHypothesisFileCommentParams): void {
    this._hypothesisFilesState.update(current => {
      if (!current || !current[type]) {
        return current;
      }

      const files = current[type];
      let hasChanges = false;

      const updatedFiles = files.map(file => {
        if (file.file.id === fileId && file.comment !== comment) {
          hasChanges = true;
          return { ...file, comment };
        }
        return file;
      });

      return hasChanges ?
        {
          ...current,
          [type]: updatedFiles
        } :
        current;
    });
  }

  public resetHypothesisFilesState(): void {
    this._hypothesisFilesState.set(null);
  }
}
